<template>
    <re-b-modal ref="modal" large no-auto-hide no-close-on-backdrop
                :header="modalHeader"
                :loading="detailLoading"
                :ok-loading="saveLoading"
                :ok-label="$t('label.em.asset.emArchFixedAsset.save')"
                @ok="save">
        <b-form>
            <b-alert :show="!!errorMessage" variant="danger" class="py-2">
                {{ errorMessage }}
            </b-alert>

            <h6 class="text-muted border-bottom pb-1">
                {{ $t('label.em.asset.emArchFixedAsset.baseBlock') }}
            </h6>
            <b-form-row>
                <b-col md="4">
                    <b-form-group :label="requiredLabel('assetNo')">
                        <b-form-input v-model="form.assetNo" size="sm" trim></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="requiredLabel('subAssetNo')">
                        <b-form-input v-model="form.subAssetNo" size="sm" trim></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="requiredLabel('assetName')">
                        <b-form-input v-model="form.assetName" size="sm" trim></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="plainLabel('equipmentCode')">
                        <b-form-input v-model="form.equipmentCode" size="sm" trim></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="requiredLabel('costCenterId')">
                        <b-form-select v-model="form.costCenterId" size="sm"
                                       :options="costCenterOptions"></b-form-select>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="plainLabel('deptName')">
                        <b-form-input :value="selectedDeptName" size="sm" readonly></b-form-input>
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group :label="requiredLabel('capitalizationDate')">
                        <b-form-datepicker v-model="form.capitalizationDate" size="sm"
                                           reset-button></b-form-datepicker>
                    </b-form-group>
                </b-col>
                <b-col md="8">
                    <b-form-group :label="plainLabel('remark')">
                        <b-form-textarea v-model="form.remark" size="sm" rows="2"></b-form-textarea>
                    </b-form-group>
                </b-col>
            </b-form-row>

            <template v-if="finance">
                <h6 class="text-muted border-bottom pb-1">
                    {{ $t('label.em.asset.emArchFixedAsset.financeBlock') }}
                </h6>
                <b-form-row>
                    <b-col md="3">
                        <b-form-group :label="requiredLabel('originalValue')">
                            <b-form-input v-model="form.originalValue" size="sm" type="number"
                                          step="0.01"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group :label="requiredLabel('accumulatedDepreciation')">
                            <b-form-input v-model="form.accumulatedDepreciation" size="sm" type="number"
                                          step="0.01"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group :label="requiredLabel('netBookValue')">
                            <b-form-input v-model="form.netBookValue" size="sm" type="number"
                                          step="0.01"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="3">
                        <b-form-group :label="requiredLabel('depreciationYears')">
                            <b-form-input v-model="form.depreciationYears" size="sm" type="number"
                                          step="1"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <!-- 折旧年限-月：SAP「年 + 期」里的期，选填；与折旧年限一样只给财务看 -->
                    <b-col md="3">
                        <b-form-group :label="plainLabel('depreciationMonths')"
                                      :description="$t('label.em.asset.emArchFixedAsset.depreciationMonthsTip')">
                            <b-form-input v-model="form.depreciationMonths" size="sm" type="number"
                                          min="0" step="1"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </template>

            <template v-if="form.id">
                <h6 class="text-muted border-bottom pb-1">
                    {{ $t('label.em.asset.emArchFixedAsset.ledgerBlock') }}
                    <span v-if="deptMismatch" class="text-danger ml-2"
                          :title="$t('label.em.asset.emArchFixedAsset.deptMismatchTip')">
                        <i class="fa fa-fw fa-exclamation-triangle"></i>
                        {{ $t('label.em.asset.emArchFixedAsset.deptMismatchTip') }}
                    </span>
                </h6>
                <b-form-row>
                    <b-col md="4">
                        <b-form-group :label="plainLabel('locationName')">
                            <b-form-input :value="readOnly.locationName" size="sm" readonly></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group :label="plainLabel('epcCode')">
                            <b-form-input :value="readOnly.epcCode" size="sm" readonly></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group :label="plainLabel('assetStatus')">
                            <b-form-input :value="assetStatusText" size="sm" readonly></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-form-row>
            </template>

            <h6 class="text-muted border-bottom pb-1">
                {{ $t('label.em.asset.emArchFixedAsset.attachmentBlock') }}
            </h6>
            <!--
                URS 需求_new R17：可上传多个文件，图片 / pdf / word 等格式。
                后端挂接在 EmArchFixedAssetServiceImpl.save 里（linkedTableName = EmArchFixedAsset）。
                attachmentIds 只在打开弹窗时喂一次，之后靠 idsChanged 单向写回 form.attachments，
                避免「回写 form → 回灌 prop → 组件重新拉列表」的来回。
            -->
            <em-attachment ref="attachment"
                           :key="attachmentKey"
                           class="w-100 mb-2"
                           get-items-by-self
                           is-editing
                           :avoid-null="false"
                           :attachment-ids="attachmentIds"
                           @idsChanged="whenAttachmentIdsChanged"></em-attachment>

            <h6 class="text-muted border-bottom pb-1">
                {{ $t('label.em.asset.emArchFixedAsset.extBlock') }}
            </h6>
            <b-form-row>
                <b-col v-for="key of extKeys" :key="key" md="4">
                    <b-form-group :label="plainLabel(key)">
                        <b-form-input v-model="form[key]" size="sm" trim></b-form-input>
                    </b-form-group>
                </b-col>
            </b-form-row>
        </b-form>
    </re-b-modal>
</template>

<script>
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import EmAttachment from "@/components/em/EmAttachment";
import emCommonApiMixin from "@/mixin/em/common/api";
import {costCenterText} from "@/views/em/asset/costCenterText";

/**
 * 固定资产档案 新增 / 编辑弹窗（spec §4）
 * <p>
 * 仅财务人员可打开（列表页按 GET /emArchFixedAsset/permission 隐藏入口），
 * 后端 EmArchAssetRoleGuard 同样拒绝非财务的写操作。
 */
export default {
    name: "EmArchFixedAssetEditModal",
    components: {EmAttachment, ReBModal},
    mixins: [emCommonApiMixin],
    props: {
        // 当前用户是否财务人员；由列表页的 permission 探测结果传入
        finance: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            detailLoading: false,
            saveLoading: false,
            errorMessage: '',
            costCenters: [],
            // 只在打开弹窗 / 拉到详情时喂给 em-attachment，之后不再跟随 form 变化
            attachmentIds: '',
            // 强制重建 em-attachment：组件的附件列表活在实例上，不重建会把上一条档案的附件带过来
            attachmentKey: 0,
            form: this.emptyForm(),
            readOnly: {
                locationName: null,
                epcCode: null,
                assetStatus: null,
                deptMismatch: null,
            },
            extKeys: ['ext1', 'ext2', 'ext3', 'ext4', 'ext5'],
            // URS 需求_new 打星号字段（部门与资产状态由后端推导，不在表单校验）
            requiredKeys: [
                'assetNo', 'subAssetNo', 'assetName', 'costCenterId',
                'originalValue', 'accumulatedDepreciation', 'netBookValue',
                'capitalizationDate', 'depreciationYears'
            ],
        }
    },
    computed: {
        baseApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset';
        },
        modalHeader() {
            return this.form.id
                    ? this.$t('title.em.asset.emArchFixedAsset.edit')
                    : this.$t('title.em.asset.emArchFixedAsset.create');
        },
        costCenterOptions() {
            let options = [{
                value: null,
                text: this.$t('label.em.asset.emArchFixedAsset.costCenterPlaceholder')
            }];
            for (let item of this.costCenters) {
                options.push({
                    value: item.id,
                    text: costCenterText(item.code, item.name)
                });
            }
            return options;
        },
        selectedCostCenter() {
            return this.costCenters.find(o => o.id === this.form.costCenterId) || null;
        },
        selectedDeptName() {
            return this.selectedCostCenter ? this.selectedCostCenter.deptName : null;
        },
        deptMismatch() {
            return this.readOnly.deptMismatch === 1;
        },
        assetStatusText() {
            let status = this.readOnly.assetStatus;
            return status ? this.$t(`label.em.asset.emArchFixedAsset.status.${status}`) : '';
        },
    },
    methods: {
        emptyForm() {
            return {
                id: null,
                assetNo: null,
                subAssetNo: null,
                assetName: null,
                equipmentCode: null,
                costCenterId: null,
                originalValue: null,
                accumulatedDepreciation: null,
                netBookValue: null,
                depreciationYears: null,
                depreciationMonths: null,
                capitalizationDate: null,
                remark: null,
                attachments: null,
                ext1: null,
                ext2: null,
                ext3: null,
                ext4: null,
                ext5: null,
            };
        },

        /**
         * row 为 null / 无 id 时是新增；否则拉详情后编辑
         */
        open(row) {
            this.errorMessage = '';
            this.form = this.emptyForm();
            this.readOnly = {locationName: null, epcCode: null, assetStatus: null, deptMismatch: null};
            this.attachmentIds = '';
            this.attachmentKey++;
            this.$refs.modal.showModal();
            this.loadCostCenters();
            if (row && row.id) {
                this.loadDetail(row.id);
            }
        },

        loadCostCenters() {
            if (this.costCenters.length > 0) {
                return;
            }
            this.$_emApiMixin_get(`${this.baseApi}/costCenters`)
                    .then(res => this.costCenters = res || []);
        },

        loadDetail(id) {
            this.detailLoading = true;
            this.$_emApiMixin_get(`${this.baseApi}/detail/${id}`)
                    .then(res => {
                        if (!res) {
                            return;
                        }
                        let form = this.emptyForm();
                        for (let key of Object.keys(form)) {
                            if (res[key] !== undefined) {
                                form[key] = res[key];
                            }
                        }
                        // 后端 spring.jackson.date-format = yyyy-MM-dd HH:mm:ss，
                        // b-form-datepicker 只认 yyyy-MM-dd，这里截掉时间部分
                        form.capitalizationDate = this.toDateOnly(res.capitalizationDate);
                        this.form = form;
                        this.attachmentIds = res.attachments || '';
                        this.attachmentKey++;
                        this.readOnly = {
                            locationName: res.locationName,
                            epcCode: res.epcCode,
                            assetStatus: res.assetStatus,
                            deptMismatch: res.deptMismatch,
                        };
                    })
                    .finally(() => this.detailLoading = false);
        },

        save() {
            let missing = this.firstMissingRequired();
            if (missing) {
                this.errorMessage = `${this.$t('label.em.asset.emArchFixedAsset.requiredTip')}：${this.$t('label.em.asset.emArchFixedAsset.entity.' + missing)}`;
                return;
            }
            if (!this.isBlankOrNonNegativeInteger(this.form.depreciationMonths)) {
                this.errorMessage = `${this.$t('label.em.asset.emArchFixedAsset.entity.depreciationMonths')}：${this.$t('label.em.asset.emArchFixedAsset.depreciationMonthsTip')}`;
                return;
            }
            // 选完文件到服务端返回附件 id 之间，新文件还不在 form.attachments 里；
            // 此时保存会提交不含新文件的 id 串，重新打开就「看不到刚上传的附件」
            let attachment = this.$refs.attachment;
            if (attachment && attachment.uploadStatus === 2) {
                this.errorMessage = this.$t('label.em.asset.emArchFixedAsset.attachmentUploading');
                return;
            }
            this.errorMessage = '';
            this.saveLoading = true;
            // api/post 返回的是完整 response，业务体在 .data（EmResponseUtils.success 是裸透传）
            this.$_emApiMixin_post(`${this.baseApi}/save`, this.buildPayload())
                    .then(response => {
                        let saved = response.data;
                        this.$bvToast.toast(this.$t('label.em.asset.emArchFixedAsset.saveSuccess'), {
                            variant: 'success',
                            noCloseButton: true,
                            autoHideDelay: 2000
                        });
                        this.$emit('saved', saved);
                        this.$refs.modal.hide();
                    })
                    .finally(() => this.saveLoading = false);
        },

        /**
         * em-attachment 上传 / 删除后回写附件 id 串（逗号分隔），随保存一起提交
         */
        whenAttachmentIdsChanged(ids) {
            this.form.attachments = ids || null;
        },

        buildPayload() {
            let payload = Object.assign({}, this.form);
            // 后端 SimpleDateFormat 是严格模式的 yyyy-MM-dd HH:mm:ss，只给日期会解析失败
            payload.capitalizationDate = payload.capitalizationDate
                    ? `${payload.capitalizationDate} 00:00:00`
                    : null;
            payload.depreciationYears = this.toNumberOrNull(payload.depreciationYears);
            payload.depreciationMonths = this.toNumberOrNull(payload.depreciationMonths);
            payload.originalValue = this.toNumberOrNull(payload.originalValue);
            payload.accumulatedDepreciation = this.toNumberOrNull(payload.accumulatedDepreciation);
            payload.netBookValue = this.toNumberOrNull(payload.netBookValue);
            return payload;
        },

        firstMissingRequired() {
            for (let key of this.requiredKeys) {
                let value = this.form[key];
                if (value === null || value === undefined || value === '') {
                    return key;
                }
            }
            return null;
        },

        isBlankOrNonNegativeInteger(value) {
            if (value === null || value === undefined || value === '') {
                return true;
            }
            let num = Number(value);
            return Number.isInteger(num) && num >= 0;
        },

        toNumberOrNull(value) {
            if (value === null || value === undefined || value === '') {
                return null;
            }
            let num = Number(value);
            return isNaN(num) ? null : num;
        },

        toDateOnly(value) {
            if (!value) {
                return null;
            }
            let text = String(value);
            return text.length >= 10 ? text.substring(0, 10) : text;
        },

        requiredLabel(key) {
            return `${this.$t('label.em.asset.emArchFixedAsset.entity.' + key)} *`;
        },

        plainLabel(key) {
            return this.$t('label.em.asset.emArchFixedAsset.entity.' + key);
        },
    }
}
</script>

<style scoped>
</style>
